const { createEnv } = require("@t3-oss/env-nextjs");
const z = require("zod");

module.exports.env = createEnv({
	server: {
		NEXT_PUBLIC_SENTRY_ENVIRONMENT: z.union([
			z.literal("local"),
			z.literal("development"),
			z.literal("staging"),
			z.literal("production"),
			z.literal("beta"),
		]),
	},
	client: {
		NEXT_PUBLIC_API_BASE_URL: z.string().min(1, "NEXT_PUBLIC_API_BASE_URL is required"),
		NEXT_PUBLIC_FILE_DOWNLOAD_BASE_URL: z.string().min(1, "NEXT_PUBLIC_FILE_DOWNLOAD_BASE_URL is required"),
		NEXT_PUBLIC_DEFAULT_ROUND_LENGTH_IN_DAYS: z
			.string()
			.min(1, "NEXT_PUBLIC_FILE_DOWNLOAD_BASE_URL is required")
			.transform((v) => parseInt(v)),
		NEXT_PUBLIC_NUMBER_OF_ROUNDS: z
			.string()
			.min(1, "NEXT_PUBLIC_NUMBER_OF_ROUNDS is required")
			.transform((v) => parseInt(v)),
		NEXT_PUBLIC_SENTRY_ENVIRONMENT: z.union([
			z.literal("local"),
			z.literal("development"),
			z.literal("staging"),
			z.literal("production"),
			z.literal("beta"),
		]),
		NEXT_PUBLIC_OCP_LANDING_URL: z.string().min(1, "NEXT_PUBLIC_OCP_LANDING_URL is required"),
		NEXT_PUBLIC_CREDITS_TRANSACTION_DETAILS_LINK: z
			.string()
			.min(1, "NEXT_PUBLIC_CREDITS_TRANSACTION_DETAILS_LINK is required"),
		NEXT_PUBLIC_ACTIVITY_FETCH_WINDOW_IN_DAYS: z.coerce
			.number()
			.min(1, "NEXT_PUBLIC_ACTIVITY_FETCH_WINDOW_IN_DAYS is required and should be at least 1"),
		NEXT_PUBLIC_VALIDATION_REPORT_TEMPLATE_LINK: z.string().url(),
		NEXT_PUBLIC_PUBLIC_COMMENTARY_JOTFORM_LINK: z.string().url(),
		NEXT_PUBLIC_PROJECTS_PUBLIC_COMMENTARY_JOTFORM_LINK: z.string().url(),
		NEXT_PUBLIC_TINYMCE_API_KEY: z.string().optional(),
		NEXT_PUBLIC_PERMANENT_ANONIMITY_VALIDATOR_IDS: z.preprocess((val) => {
			if (typeof val !== "string") return [];
			return val
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean)
				.map((s) => Number(s));
		}, z.array(z.number().int())),
		NEXT_PUBLIC_VALIDATOR_IDS_WITH_PRIVATE_REPORTS: z.preprocess((val) => {
			if (typeof val !== "string") return [];
			return val
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean)
				.map((s) => Number(s));
		}, z.array(z.number().int())),
		NEXT_PUBLIC_CONVERTED_MTD_IDS: z.preprocess((val) => {
			if (typeof val !== "string") return [];
			return val
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean)
				.map((s) => Number(s));
		}, z.array(z.number().int())),
		NEXT_PUBLIC_OCP_MTD_PUBLISHED_COUNT: z.coerce
			.number()
			.min(0, { message: "NEXT_PUBLIC_OCP_MTD_PUBLISHED_COUNT must be at least 0" }),
		NEXT_PUBLIC_OCP_MTD_PUBLIC_COMMENTARY_COUNT: z.coerce
			.number()
			.min(0, { message: "NEXT_PUBLIC_OCP_MTD_PUBLIC_COMMENTARY_COUNT must be at least 0" }),
		NEXT_PUBLIC_OCP_MTD_IN_REVIEW_COUNT: z.coerce
			.number()
			.min(0, { message: "NEXT_PUBLIC_OCP_MTD_IN_REVIEW_COUNT must be at least 0" }),
		NEXT_PUBLIC_OCP_MTD_UNDER_DEVELOPMENT_COUNT: z.coerce
			.number()
			.min(0, { message: "NEXT_PUBLIC_OCP_MTD_UNDER_DEVELOPMENT_COUNT must be at least 0" }),
		NEXT_PUBLIC_OCP_MTD_UPCOMING_COUNT: z.coerce
			.number()
			.min(0, { message: "NEXT_PUBLIC_OCP_MTD_UPCOMING_COUNT must be at least 0" }),
		NEXT_PUBLIC_OCP_PROJECTS_ACTIVE_COUNT: z.coerce
			.number()
			.min(0, { message: "NEXT_PUBLIC_OCP_PROJECTS_ACTIVE_COUNT must be at least 0" }),
		NEXT_PUBLIC_OCP_PROJECTS_IN_VALIDATION_COUNT: z.coerce
			.number()
			.min(0, { message: "NEXT_PUBLIC_OCP_PROJECTS_IN_VALIDATION_COUNT must be at least 0" }),
		NEXT_PUBLIC_FORCEFULLY_HIDDEN_PUBLIC_COMMENTARY_MTD_IDS: z.preprocess((val) => {
			if (typeof val !== "string") return [];
			return val
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean)
				.map((s) => Number(s));
		}, z.array(z.number().int())),
		NEXT_PUBLIC_MAINTENANCE_MODE: z
			.string()
			.optional()
			.transform((val) => val === "true")
			.default(false),
		NEXT_PUBLIC_FORCE_DISABLE_MTD_FINAL_ROUND_SUBMISSION: z
			.string()
			.optional()
			.transform((val) => val === "true")
			.default(false),
		NEXT_PUBLIC_BLOCK_PAGES_FOR_UNLOGGED_USERS: z
			.string()
			.optional()
			.transform((val) => val === "true")
			.default(false),
		NEXT_PUBLIC_SHOW_DEV_STAGING_WARNING_IN_AUTH_PAGE: z
			.string()
			.optional()
			.transform((val) => val === "true")
			.default(false),
		// JSON array of governance documents currently open for public commentary.
		// Empty or omitted hides the open-document list.
		NEXT_PUBLIC_GOVERNANCE_PUBLIC_COMMENTARY: z.string().optional().default(""),
	},
	runtimeEnv: {
		NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
		NEXT_PUBLIC_FILE_DOWNLOAD_BASE_URL: process.env.NEXT_PUBLIC_FILE_DOWNLOAD_BASE_URL,
		NEXT_PUBLIC_DEFAULT_ROUND_LENGTH_IN_DAYS: process.env.NEXT_PUBLIC_DEFAULT_ROUND_LENGTH_IN_DAYS,
		NEXT_PUBLIC_NUMBER_OF_ROUNDS: process.env.NEXT_PUBLIC_NUMBER_OF_ROUNDS,
		NEXT_PUBLIC_SENTRY_ENVIRONMENT: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
		NEXT_PUBLIC_OCP_LANDING_URL: process.env.NEXT_PUBLIC_OCP_LANDING_URL,
		NEXT_PUBLIC_CREDITS_TRANSACTION_DETAILS_LINK: process.env.NEXT_PUBLIC_CREDITS_TRANSACTION_DETAILS_LINK,
		NEXT_PUBLIC_ACTIVITY_FETCH_WINDOW_IN_DAYS: process.env.NEXT_PUBLIC_ACTIVITY_FETCH_WINDOW_IN_DAYS,
		NEXT_PUBLIC_VALIDATION_REPORT_TEMPLATE_LINK: process.env.NEXT_PUBLIC_VALIDATION_REPORT_TEMPLATE_LINK,
		NEXT_PUBLIC_PUBLIC_COMMENTARY_JOTFORM_LINK: process.env.NEXT_PUBLIC_PUBLIC_COMMENTARY_JOTFORM_LINK,
		NEXT_PUBLIC_PROJECTS_PUBLIC_COMMENTARY_JOTFORM_LINK:
			process.env.NEXT_PUBLIC_PROJECTS_PUBLIC_COMMENTARY_JOTFORM_LINK,
		NEXT_PUBLIC_TINYMCE_API_KEY: process.env.NEXT_PUBLIC_TINYMCE_API_KEY,
		NEXT_PUBLIC_PERMANENT_ANONIMITY_VALIDATOR_IDS: process.env.NEXT_PUBLIC_PERMANENT_ANONIMITY_VALIDATOR_IDS,
		NEXT_PUBLIC_VALIDATOR_IDS_WITH_PRIVATE_REPORTS: process.env.NEXT_PUBLIC_VALIDATOR_IDS_WITH_PRIVATE_REPORTS,
		NEXT_PUBLIC_CONVERTED_MTD_IDS: process.env.NEXT_PUBLIC_CONVERTED_MTD_IDS,
		NEXT_PUBLIC_OCP_MTD_PUBLISHED_COUNT: process.env.NEXT_PUBLIC_OCP_MTD_PUBLISHED_COUNT,
		NEXT_PUBLIC_OCP_MTD_PUBLIC_COMMENTARY_COUNT: process.env.NEXT_PUBLIC_OCP_MTD_PUBLIC_COMMENTARY_COUNT,
		NEXT_PUBLIC_OCP_MTD_IN_REVIEW_COUNT: process.env.NEXT_PUBLIC_OCP_MTD_IN_REVIEW_COUNT,
		NEXT_PUBLIC_OCP_MTD_UNDER_DEVELOPMENT_COUNT: process.env.NEXT_PUBLIC_OCP_MTD_UNDER_DEVELOPMENT_COUNT,
		NEXT_PUBLIC_OCP_MTD_UPCOMING_COUNT: process.env.NEXT_PUBLIC_OCP_MTD_UPCOMING_COUNT,
		NEXT_PUBLIC_OCP_PROJECTS_ACTIVE_COUNT: process.env.NEXT_PUBLIC_OCP_PROJECTS_ACTIVE_COUNT,
		NEXT_PUBLIC_OCP_PROJECTS_IN_VALIDATION_COUNT: process.env.NEXT_PUBLIC_OCP_PROJECTS_IN_VALIDATION_COUNT,
		NEXT_PUBLIC_FORCEFULLY_HIDDEN_PUBLIC_COMMENTARY_MTD_IDS:
			process.env.NEXT_PUBLIC_FORCEFULLY_HIDDEN_PUBLIC_COMMENTARY_MTD_IDS,
		NEXT_PUBLIC_MAINTENANCE_MODE: process.env.NEXT_PUBLIC_MAINTENANCE_MODE,
		NEXT_PUBLIC_FORCE_DISABLE_MTD_FINAL_ROUND_SUBMISSION:
			process.env.NEXT_PUBLIC_FORCE_DISABLE_MTD_FINAL_ROUND_SUBMISSION,
		NEXT_PUBLIC_BLOCK_PAGES_FOR_UNLOGGED_USERS: process.env.NEXT_PUBLIC_BLOCK_PAGES_FOR_UNLOGGED_USERS,
		NEXT_PUBLIC_SHOW_DEV_STAGING_WARNING_IN_AUTH_PAGE: process.env.NEXT_PUBLIC_SHOW_DEV_STAGING_WARNING_IN_AUTH_PAGE,
		NEXT_PUBLIC_GOVERNANCE_PUBLIC_COMMENTARY: process.env.NEXT_PUBLIC_GOVERNANCE_PUBLIC_COMMENTARY,
	},
});
